import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IoChevronDown } from "react-icons/io5";
import { supabase } from "../supabase";
import { friendlyError } from "../utils/errors";

const FAQS = [
  {
    q: "What is GatherYou?",
    a: "GatherYou is a place to share and discover Punjabi fashion and embroidery designs — post your own suits, jutti, phulkari, and bridal work, and follow other designers for inspiration."
  },
  {
    q: "How do I post a design?",
    a: 'Tap the + button in the bottom navigation. Add up to 10 photos, an optional embroidery/pattern file (like .dst or .pes), and a caption — you can add hashtags with # and mention other users with @.'
  },
  {
    q: "How do downloads work?",
    a: "If a post includes an embroidery or pattern file, tap the download icon to save it to your device. The original poster is notified when someone downloads their file."
  },
  {
    q: "Can I control who sees my followers/following list?",
    a: 'Yes — in Profile settings (⚙️), you can toggle "Show followers" and "Show following" independently.'
  },
  {
    q: "How do I save designs for later?",
    a: 'Tap the bookmark icon on any post. Saved designs show up on your Saved page, where you can also organize them into folders (like "Wedding" or "Casual").'
  },
  {
    q: "Someone is bothering me — what can I do?",
    a: "Open their profile or the comment in question and use the report or block option. Blocking hides their content from you and yours from them; reporting sends it to us for review."
  },
  {
    q: "How do I turn on notifications?",
    a: "In Profile settings, turn on \"Push notifications\" to get notified even when the app isn't open, in addition to the in-app bell."
  },
  {
    q: "Can I use GatherYou in dark mode?",
    a: 'Yes — toggle "Dark mode" in Profile settings.'
  },
  {
    q: "How do I delete my account?",
    a: "In Profile settings, choose \"Delete account.\" This permanently removes your profile, posts, and account — it can't be undone."
  }
];

export default function Help() {
  const [openIndex, setOpenIndex] = useState(null);
  const [user, setUser] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  // disabled={feedbackSubmitting} on the submit button only takes effect
  // next render — this had no guard at all beforehand, so a fast
  // double-click inserted the same feedback message twice.
  const feedbackSubmittingRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
    });
  }, []);

  async function submitFeedback(e) {
    e.preventDefault();
    if (feedbackSubmittingRef.current) return;
    setFeedbackError(null);

    const clean = feedbackText.trim();
    if (!clean) {
      setFeedbackError("Write a quick description first.");
      return;
    }

    feedbackSubmittingRef.current = true;
    setFeedbackSubmitting(true);

    const { error } = await supabase.from("feedback").insert({
      user_id: user?.id || null,
      message: clean
    });

    feedbackSubmittingRef.current = false;
    setFeedbackSubmitting(false);

    if (error) {
      console.log("FEEDBACK ERROR:", error);
      setFeedbackError(friendlyError(error, "Couldn't send that. Try again."));
      return;
    }

    setFeedbackText("");
    setFeedbackSent(true);
  }

  return (
    <div style={page}>
      <h1 style={heading}>Help &amp; FAQ</h1>
      <p style={intro}>
        Quick answers to common questions. Still stuck? Email us at{" "}
        <a href="mailto:contact@gatheryou.in" style={link}>contact@gatheryou.in</a>.
      </p>

      <div style={faqList}>
        {FAQS.map((item, i) => {
          const open = openIndex === i;
          return (
            <div key={item.q} style={faqItem}>
              <button
                style={faqQuestion}
                onClick={() => setOpenIndex(open ? null : i)}
                aria-expanded={open}
              >
                <span>{item.q}</span>
                <IoChevronDown
                  style={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.15s ease",
                    flexShrink: 0
                  }}
                />
              </button>
              {open && <p style={faqAnswer}>{item.a}</p>}
            </div>
          );
        })}
      </div>

      <h2 style={feedbackHeading}>Report a bug or send feedback</h2>
      <p style={intro}>
        Found something broken, or have an idea for the app? Tell us about it.
      </p>

      {feedbackSent ? (
        <p style={feedbackSentText}>Thanks — we've got it.</p>
      ) : (
        <form onSubmit={submitFeedback} style={feedbackForm}>
          <textarea
            style={feedbackTextarea}
            placeholder="What happened, or what would you like to see?"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            maxLength={1000}
          />
          {feedbackError && <p style={feedbackErrorText}>{feedbackError}</p>}
          <button type="submit" style={feedbackSubmitBtn} disabled={feedbackSubmitting}>
            {feedbackSubmitting ? "Sending…" : "Send"}
          </button>
        </form>
      )}

      <p style={backLink}>
        <Link to="/terms" style={link}>Terms</Link>
        {" · "}
        <Link to="/privacy" style={link}>Privacy</Link>
        {" · "}
        <Link to="/" style={link}>Back to GatherYou</Link>
      </p>
    </div>
  );
}

const page = {
  padding: "24px 20px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 680,
  margin: "0 auto",
  background: "var(--bg-page)",
  minHeight: "100vh",
};

const heading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 26,
  color: "var(--text-heading)",
  margin: "0 0 8px",
};

const intro = {
  fontSize: 14,
  color: "var(--text-body)",
  lineHeight: 1.6,
  margin: "0 0 24px",
};

const faqList = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const faqItem = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 12,
  overflow: "hidden",
};

const faqQuestion = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "14px 16px",
  border: "none",
  background: "none",
  textAlign: "left",
  fontSize: 14.5,
  fontWeight: 700,
  color: "var(--text-heading)",
  cursor: "pointer",
};

const faqAnswer = {
  margin: 0,
  padding: "0 16px 16px",
  fontSize: 13.5,
  color: "var(--text-body)",
  lineHeight: 1.6,
};

const backLink = {
  fontSize: 13.5,
  color: "var(--text-muted)",
  marginTop: 32,
  textAlign: "center",
};

const link = { color: "var(--accent-link)", fontWeight: 600, textDecoration: "none" };

const feedbackHeading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 19,
  color: "var(--text-heading)",
  margin: "32px 0 6px",
};

const feedbackForm = { display: "flex", flexDirection: "column", gap: 10 };

const feedbackTextarea = {
  minHeight: 90,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 14,
  resize: "vertical",
  outline: "none",
};

const feedbackSubmitBtn = {
  alignSelf: "flex-start",
  padding: "10px 22px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 13.5,
  cursor: "pointer",
};

const feedbackErrorText = { fontSize: 12.5, color: "var(--accent-danger)", margin: 0 };

const feedbackSentText = {
  fontSize: 14,
  color: "var(--accent-success)",
  fontWeight: 600,
};
